import { Button, Tag } from 'antd';
import { COLOR_OPTIONS } from 'config/style';
import { RiskLevelEnum } from 'querys/regional-overview/regional-overview.query.models';
import styled, { createGlobalStyle } from 'styled-components';

export const FieldDetailsPopupStyles = createGlobalStyle`
  .field-details-popup {
    .mapboxgl-popup-tip {
      display: none;
    }

    .mapboxgl-popup-content {
      padding: 0;
      background: transparent;
      box-shadow: none;
    }
  }
`;

export const Container = styled.article`
  display: flex;
  flex-direction: column;
  width: 377px;
  border: 1px solid ${COLOR_OPTIONS.NEUTRAL_30};
  border-radius: 6px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
`;

interface HeaderProps {
  $riskLevel?: RiskLevelEnum;
  $isMissingConfiguration?: boolean;
  $sheetTopGap?: boolean;
}

export const Header = styled.header<HeaderProps>`
  display: grid;
  grid-template-columns: 1fr auto auto;
  grid-template-rows: auto;
  align-items: center;
  padding: 10px;
  ${({ $sheetTopGap }) => $sheetTopGap && 'padding-top: 18px;'}
  background-color: ${({ $riskLevel, $isMissingConfiguration }) => {
    if ($isMissingConfiguration) {
      return COLOR_OPTIONS.NEUTRAL_20;
    }
    switch ($riskLevel) {
      case RiskLevelEnum.LOW:
        return COLOR_OPTIONS.GREEN_20;
      case RiskLevelEnum.MEDIUM:
        return COLOR_OPTIONS.YELLOW_20;
      case RiskLevelEnum.HIGH:
        return COLOR_OPTIONS.RED_20;
      case RiskLevelEnum.NO_RISK:
        return COLOR_OPTIONS.NEUTRAL_00;
      default:
        return COLOR_OPTIONS.NEUTRAL_20;
    }
  }};
  gap: 8px;
  position: relative;
`;

interface CropIconWrapperProps {
  $riskLevel?: RiskLevelEnum;
  $isMissingConfiguration?: boolean;
}

export const CropIconWrapper = styled.div<CropIconWrapperProps>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: ${({ $riskLevel, $isMissingConfiguration }) => {
    if ($isMissingConfiguration) {
      return COLOR_OPTIONS.NEUTRAL_00;
    }
    switch ($riskLevel) {
      case RiskLevelEnum.LOW:
        return COLOR_OPTIONS.GREEN_50;
      case RiskLevelEnum.MEDIUM:
        return COLOR_OPTIONS.YELLOW_40;
      case RiskLevelEnum.HIGH:
        return COLOR_OPTIONS.RED_60;
      case RiskLevelEnum.NO_RISK:
        return COLOR_OPTIONS.GREEN_60;
      default:
        return COLOR_OPTIONS.NEUTRAL_50;
    }
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;

  svg {
    width: 32px;
    height: 32px;

    path {
      ${({ $isMissingConfiguration }) => {
        if ($isMissingConfiguration) {
          return `fill: ${COLOR_OPTIONS.GRAY_2};`;
        } else {
          return `fill: ${COLOR_OPTIONS.NEUTRAL_00};`;
        }
      }}
    }
  }
`;

interface RiskTagProps {
  $riskLevel: RiskLevelEnum;
}

export const RiskTag = styled(Tag)<RiskTagProps>`
  &&.ant-tag {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 3px 6px;
    margin-inline-end: 0;
    background-color: ${({ $riskLevel }) => {
      switch ($riskLevel) {
        case RiskLevelEnum.LOW:
          return COLOR_OPTIONS.GREEN_50;
        case RiskLevelEnum.MEDIUM:
          return COLOR_OPTIONS.YELLOW_40;
        case RiskLevelEnum.HIGH:
          return COLOR_OPTIONS.RED_60;
        case RiskLevelEnum.NO_RISK:
          return COLOR_OPTIONS.GREEN_60;
        default:
          return COLOR_OPTIONS.NEUTRAL_50;
      }
    }};
    border-radius: 4px;
    font-family: 'Noto Sans', sans-serif;
    font-size: 14px;
    line-height: 16px;
    font-weight: 600;
    color: ${COLOR_OPTIONS.NEUTRAL_00};
    white-space: nowrap;
    border: none;
  }
`;

export const CloseButton = styled(Button)`
  && {
    padding: 0;
    margin-left: 10px;
    display: flex;
    align-items: center;
    align-self: start;
    justify-content: center;
    background: none;
    border: none;
    color: ${COLOR_OPTIONS.SECONDARY};
    height: auto;
    min-width: auto;
    box-shadow: none;

    &:hover {
      color: ${COLOR_OPTIONS.NEUTRAL_100};
      background: none;
      border: none;
    }

    svg {
      width: 20px;
      height: 20px;
      fill: ${COLOR_OPTIONS.SECONDARY};
    }
  }
`;

export const DetailsLink = styled(Button)`
  font-family: 'Noto Sans', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 16px;
  color: ${COLOR_OPTIONS.BLUE_60};
  padding: 0;
  height: auto;
  text-decoration: underline;
  text-decoration-skip-ink: none;
  text-underline-position: from-font;

  &:hover {
    color: ${COLOR_OPTIONS.BLUE_70};
    text-decoration: underline;
  }
`;
